import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import { type ReactNode, useState } from 'react';

import { Button } from '@revelio/ui';

import { CustomColumnModal } from '../../components/custom-columns/CustomColumnModal';
import {
  AddCustomColumnOption,
  CustomColumnCard,
} from '../../components/custom-columns/CustomColumnOption';
import { DataBuilderGrid } from '../../components/ui/DataBuilderGrid';
import { LabeledGroup } from '../../components/ui/LabeledGroup';
import type { DatasetInstanceConfig } from '../../data-builder.store';
import { useDataBuilderActions } from '../../data-builder.store';
import type {
  AggregationColumnItem,
  Breakdown,
} from '../../datasets/deliverable-column-config';
import { STEP2_VALIDATION_SECTION_FILES } from '../step2-columns-validation';
import { ColumnGroupCard, ColumnGroupModal } from './ColumnsSections';
import { ColumnOptionCard } from './SelectionOptions';
import { Step2SectionValidationError } from './Step2SectionValidationError';
import { useResolveFilterLabels } from './useResolveFilterLabels';

type FileSectionProps = {
  file: Breakdown;
  selectedColumnKeys: string[];
  onSetColumnKey: (key: string, selected: boolean) => void;
  activeConfig: DatasetInstanceConfig;
  activeInstanceId: string | null;
  showValidationErrors: boolean;
  sectionErrorMessage: string | undefined;
  onRemove?: () => void;
  endSlot?: ReactNode;
};

type FileSectionsProps = {
  files: Breakdown[];
  addableFiles: Breakdown[];
  activeConfig: DatasetInstanceConfig;
  activeInstanceId: string | null;
  showValidationErrors: boolean;
  sectionErrors: Record<string, string>;
  onSetColumnKey: (fileId: string, key: string, selected: boolean) => void;
  onRemoveFile: (fileId: string) => void;
  onAddFile: (fileId: string) => void;
};

export const FileSections = ({
  files,
  addableFiles,
  activeConfig,
  activeInstanceId,
  showValidationErrors,
  sectionErrors,
  onSetColumnKey,
  onRemoveFile,
  onAddFile,
}: FileSectionsProps) => {
  const totalFileCount = files.length + addableFiles.length;
  const filesSectionError = sectionErrors[STEP2_VALIDATION_SECTION_FILES];
  const addFileSlot = (
    <AddFileSlot
      addableFiles={addableFiles}
      onAddFile={onAddFile}
      showValidationErrors={showValidationErrors}
      filesSectionError={filesSectionError}
    />
  );

  if (files.length === 0) {
    return addFileSlot;
  }

  return (
    <>
      {files.map((file, index) => {
        const isLast = index === files.length - 1;
        return (
          <FileSection
            key={file.id}
            file={file}
            activeConfig={activeConfig}
            activeInstanceId={activeInstanceId}
            selectedColumnKeys={
              activeConfig.columnKeysByBreakdown[file.id] ?? []
            }
            onSetColumnKey={(key, selected) =>
              onSetColumnKey(file.id, key, selected)
            }
            showValidationErrors={showValidationErrors}
            sectionErrorMessage={sectionErrors[file.id]}
            onRemove={
              totalFileCount > 1 ? () => onRemoveFile(file.id) : undefined
            }
            endSlot={isLast ? addFileSlot : undefined}
          />
        );
      })}
    </>
  );
};

const FileSection = ({
  file,
  selectedColumnKeys,
  onSetColumnKey,
  activeConfig,
  activeInstanceId,
  showValidationErrors,
  sectionErrorMessage,
  onRemove,
  endSlot,
}: FileSectionProps) => {
  const [openGroupName, setOpenGroupName] = useState<string | null>(null);
  const [customColumnModalState, setCustomColumnModalState] = useState<
    { mode: 'add' } | { mode: 'edit'; index: number } | null
  >(null);
  const {
    updateBreakdownCustomFilter,
    addCustomColumn,
    updateCustomColumn,
    removeCustomColumn,
  } = useDataBuilderActions();

  const openGroup =
    openGroupName !== null
      ? file.columnItems.find(
          (item): item is AggregationColumnItem & { type: 'group' } =>
            item.type === 'group' && item.name === openGroupName
        )
      : undefined;
  const customColumns = activeConfig.customColumnsByBreakdown[file.id] ?? [];
  const editingCustomColumn =
    customColumnModalState?.mode === 'edit'
      ? customColumns[customColumnModalState.index]
      : undefined;

  useResolveFilterLabels({
    filters: activeConfig.customFiltersByBreakdown[file.id] ?? [],
    filterDefinitions: file.customFilterDefinitions,
    onUpdateFilter: (index, filter) => {
      if (activeInstanceId !== null) {
        updateBreakdownCustomFilter(activeInstanceId, file.id, index, filter);
      }
    },
    customColumns,
    onUpdateCustomColumn: (index, column) => {
      if (activeInstanceId !== null) {
        updateCustomColumn(activeInstanceId, file.id, index, column);
      }
    },
  });

  return (
    <Box
      as="section"
      data-testid={`data-builder-file-section-${file.id}`}
      position="relative"
    >
      {onRemove !== undefined && (
        <IconButton
          aria-label={`Remove ${file.label}`}
          data-testid={`data-builder-file-remove-${file.id}`}
          icon={<CloseIcon boxSize={2.5} />}
          size="xs"
          variant="ghost"
          colorScheme="red"
          position="absolute"
          top={-0.5}
          right={-1}
          zIndex={1}
          onClick={onRemove}
          backgroundColor="white"
        />
      )}
      <LabeledGroup groupName={file.label}>
        <Flex direction="column" gap={4}>
          {file.columnItems.length > 0 && (
            <DataBuilderGrid columns="responsive">
              {file.columnItems.map((item) => {
                if (item.type === 'column') {
                  const isSelected = selectedColumnKeys.includes(
                    item.option.key
                  );
                  return (
                    <ColumnOptionCard
                      key={item.option.key}
                      keyId={item.option.key}
                      label={item.option.label}
                      description={item.option.description}
                      isRequired={item.option.isRequired}
                      isSelected={isSelected}
                      onToggle={() =>
                        onSetColumnKey(item.option.key, !isSelected)
                      }
                    />
                  );
                }

                const selectedCount = item.options.filter((option) =>
                  selectedColumnKeys.includes(option.key)
                ).length;

                return (
                  <ColumnGroupCard
                    key={item.name}
                    name={item.name}
                    description={item.description}
                    selectedCount={selectedCount}
                    totalCount={item.options.length}
                    onClick={() => setOpenGroupName(item.name)}
                  />
                );
              })}
              {file.supportsCustomColumns &&
                customColumns.map((customColumn, index) => (
                  <CustomColumnCard
                    key={customColumn.id}
                    column={customColumn}
                    onClick={() =>
                      setCustomColumnModalState({ mode: 'edit', index })
                    }
                    onRemove={() => {
                      if (activeInstanceId !== null) {
                        removeCustomColumn(activeInstanceId, file.id, index);
                      }
                    }}
                  />
                ))}
              {file.supportsCustomColumns && (
                <AddCustomColumnOption
                  onClick={() => setCustomColumnModalState({ mode: 'add' })}
                />
              )}
            </DataBuilderGrid>
          )}

          <ColumnGroupModal
            isOpen={openGroup !== undefined}
            groupName={openGroup?.name ?? ''}
            options={openGroup?.options ?? []}
            selectedColumnKeys={selectedColumnKeys}
            maxSelected={openGroup?.maxSelected ?? null}
            onSetColumnKey={onSetColumnKey}
            onClose={() => setOpenGroupName(null)}
          />
          {file.supportsCustomColumns && (
            <CustomColumnModal
              isOpen={customColumnModalState !== null}
              onClose={() => setCustomColumnModalState(null)}
              availableFilters={file.customColumnFilterDefinitions}
              availableFilterCategories={file.customColumnFilterCategories}
              existingColumn={editingCustomColumn}
              existingColumnNames={customColumns
                .filter((_, idx) => {
                  if (customColumnModalState?.mode !== 'edit') {
                    return true;
                  }
                  return idx !== customColumnModalState.index;
                })
                .map((column) => column.name)}
              onSubmit={(column) => {
                if (activeInstanceId === null) {
                  return;
                }
                if (customColumnModalState?.mode === 'edit') {
                  updateCustomColumn(
                    activeInstanceId,
                    file.id,
                    customColumnModalState.index,
                    column
                  );
                  return;
                }
                addCustomColumn(activeInstanceId, file.id, column);
              }}
            />
          )}
        </Flex>
      </LabeledGroup>

      <Step2SectionValidationError
        sectionId={file.id}
        message={sectionErrorMessage}
        show={showValidationErrors}
      />

      {endSlot}
    </Box>
  );
};

type AddFileSlotProps = {
  addableFiles: Breakdown[];
  onAddFile: (fileId: string) => void;
  showValidationErrors: boolean;
  filesSectionError: string | undefined;
};

const AddFileSlot = ({
  addableFiles,
  onAddFile,
  showValidationErrors,
  filesSectionError,
}: AddFileSlotProps) => {
  const hasAddableFiles = addableFiles.length > 0;
  const errorNode = (
    <Step2SectionValidationError
      sectionId={STEP2_VALIDATION_SECTION_FILES}
      message={filesSectionError}
      show={showValidationErrors}
    />
  );

  if (!hasAddableFiles) {
    return errorNode;
  }

  return (
    <Box width="100%" mt={2}>
      <Menu placement="bottom-start" isLazy>
        <MenuButton
          as={Box}
          display="inline-block"
          data-testid="data-builder-add-file"
          cursor="pointer"
        >
          <Button variant="ghost" color="lightBlue">
            + Add Component
          </Button>
        </MenuButton>
        <MenuList maxH="220px" overflowY="auto">
          {addableFiles.map((file) => (
            <MenuItem
              key={file.id}
              data-testid={`data-builder-add-file-option-${file.id}`}
              onClick={() => onAddFile(file.id)}
            >
              {file.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      {errorNode}
    </Box>
  );
};
